import { CheckCircle, Loader2, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createLogger } from "../../lib/logger";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

const log = createLogger("OAuthCallback");

export const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing"
  );
  const [message, setMessage] = useState("Processing authentication...");
  const [details, setDetails] = useState<string>("");

  // Add immediate console log to confirm component is rendering
  log.debug("OAuthCallback: Component is rendering!");

  useEffect(() => {
    const handleCallback = async () => {
      log.debug("OAuthCallback: Component mounted, starting callback process");
      log.debug("OAuthCallback: Search params", searchParams.toString());

      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");
      const connected = searchParams.get("connected");
      const provider =
        searchParams.get("provider") ||
        sessionStorage.getItem("oauth_provider") ||
        "github";
      const accountId = searchParams.get("account_id");

      log.debug("OAuthCallback: Extracted params", {
        code: code ? code.substring(0, 10) + "..." : null,
        state: state ? state.substring(0, 10) + "..." : null,
        error,
        errorDescription,
        connected,
        provider,
        accountId,
      });

      // Handle error from GitHub
      if (error) {
        setStatus("error");
        setMessage(`Authentication failed: ${error}`);
        setDetails(errorDescription || "Please try connecting again");
        return;
      }

      // Handle success redirect from backend (when using backend-based flow)
      if (connected === "true") {
        log.debug("OAuthCallback: Success redirect received from backend");
        const alreadyConnected = searchParams.get("already_connected") === "true";
        const providerLabel =
          provider.charAt(0).toUpperCase() + provider.slice(1);
        setStatus("success");
        setMessage(
          alreadyConnected
            ? `This ${providerLabel} account was already connected!`
            : "Successfully connected your Git account!"
        );
        setDetails(
          alreadyConnected
            ? `We refreshed your existing ${providerLabel} account. To add a different account, sign in to it on ${provider}.com first.`
            : `Your ${providerLabel} account has been linked and is ready for use.`
        );

        // Clean up session storage
        sessionStorage.removeItem("oauth_provider");

        // Redirect to integrations after 3 seconds, preserving whether this was a
        // new vs. already-connected account so the dashboard can echo it.
        setTimeout(() => {
          navigate(
            `/integrations?connected=true&already_connected=${alreadyConnected}`
          );
        }, 3000);
        return;
      }

      // GitHub App install started outside Avahana — we can't bind it to a user.
      if (connected === "false" && searchParams.get("reason") === "no_state") {
        setStatus("error");
        setMessage("Installation could not be linked to your account");
        setDetails(
          "It looks like the GitHub App was installed directly from GitHub. " +
            "Please start the connection from this page instead — click Connect " +
            "on the GitHub card so we can link the installation to your account."
        );
        return;
      }

      // OAuth ("full access") connections land here with code+state — forward
      // them to the backend callback. GitHub App installs never hit this
      // branch (the Setup URL goes straight to the backend, which redirects
      // back with ?connected=true handled above).
      if (code && state) {
        log.debug("OAuthCallback: Processing OAuth code with backend redirect");

        try {
          setStatus("processing");
          setMessage("Processing your connection...");
          setDetails("Redirecting to backend for OAuth processing...");

          // Instead of calling API, redirect to backend callback endpoint
          // The backend will process the OAuth and redirect back here with success/error params
          const backendCallbackUrl = `${
            process.env.REACT_APP_API_URL || "http://localhost:8003/api"
          }/git/callback/${provider}?code=${encodeURIComponent(
            code
          )}&state=${encodeURIComponent(state)}`;

          log.debug(
            "OAuthCallback: Redirecting to backend",
            backendCallbackUrl
          );
          window.location.href = backendCallbackUrl;
          return;
        } catch (error: any) {
          log.error("OAuthCallback: Redirect failed", error);
          setStatus("error");
          setMessage("Connection failed");
          setDetails(
            error.message || "Failed to connect. Please try again."
          );
        }
        return;
      } // If we reach here, no valid parameters were found
      setStatus("error");
      setMessage("OAuth flow incomplete");
      setDetails(
        "The OAuth flow was not completed properly. Please try again."
      );
    };

    handleCallback();
  }, [searchParams, navigate]);

  const getIcon = () => {
    switch (status) {
      case "processing":
        return <Loader2 className="h-12 w-12 text-primary animate-spin" />;
      case "success":
        return <CheckCircle className="h-12 w-12 text-success" />;
      case "error":
        return <XCircle className="h-12 w-12 text-destructive" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "processing":
        return "text-primary";
      case "success":
        return "text-success";
      case "error":
        return "text-destructive";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="flex justify-center">{getIcon()}</div>

            <div className="space-y-2">
              <h2 className={`text-h3 ${getStatusColor()}`}>
                {status === "processing" && "Connecting your account..."}
                {status === "success" && "Connection Successful!"}
                {status === "error" && "Connection Failed"}
              </h2>

              <p className="text-sm text-muted-foreground">{message}</p>

              {details && (
                <p className="text-xs text-muted-foreground mt-2">{details}</p>
              )}
            </div>

            {status === "success" && (
              <div className="space-y-2">
                <p className="text-sm text-success">
                  Redirecting to dashboard in a few seconds...
                </p>
                <Button
                  onClick={() => navigate("/integrations")}
                  className="w-full"
                >
                  Go to Integrations Now
                </Button>
              </div>
            )}

            {status === "error" && (
              <div className="space-y-2">
                {message.includes("Authentication Required") ? (
                  <Button onClick={() => navigate("/")} className="w-full">
                    Login to Continue
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => navigate("/integrations")}
                      variant="outline"
                      className="w-full"
                    >
                      Return to Integrations
                    </Button>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="secondary"
                      className="w-full"
                    >
                      Try Again
                    </Button>
                  </>
                )}
              </div>
            )}

            {status === "processing" && (
              <Button
                onClick={() => navigate("/integrations")}
                variant="ghost"
                className="w-full"
              >
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
